import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { TEMPLATE_PAYLOAD_FIELDS } from './model';

/**
 * E34 · the worksheet template commands from the portal — DefineWorksheetTemplate,
 * ReviseWorksheetTemplate, RetireWorksheetTemplate (the practice's Builder and library) and
 * PublishWorksheetTemplate (the backoffice) — each ONE deliberate act (a button), posted with
 * `errorNotification: false` so the screen reports the outcome itself (portal conventions): a CODED
 * refusal (`homework.errors.WORKSHEET_*`, already in the therapist's language) is shown verbatim,
 * against the card or setting it names; anything else is the screen's own copy plus Sentry.
 */
export type WorksheetOperation =
  | 'defineWorksheetTemplate'
  | 'reviseWorksheetTemplate'
  | 'retireWorksheetTemplate'
  | 'publishWorksheetTemplate';

const INPUT_TYPE: Record<WorksheetOperation, string> = {
  defineWorksheetTemplate: 'DefineWorksheetTemplateInput!',
  reviseWorksheetTemplate: 'ReviseWorksheetTemplateInput!',
  retireWorksheetTemplate: 'RetireWorksheetTemplateInput!',
  publishWorksheetTemplate: 'PublishWorksheetTemplateInput!',
};

export type WorksheetCommandResult =
  | { ok: true; templateId: string; version: number | null; recorded: boolean }
  | { ok: false; code: string | undefined; message: string };

export const useWorksheetCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{
    templateId: string;
    version: number | null;
    recorded: boolean;
  }>();

  const run = useCallback(
    async (
      operation: WorksheetOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<WorksheetCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: TEMPLATE_PAYLOAD_FIELDS,
            variables: {
              input: { name: 'input', type: INPUT_TYPE[operation], value: input },
            },
          },
        });
        const data = res?.data;
        if (!data?.templateId) {
          // a null answer is a refusal, never a save (portal conventions)
          return { ok: false, code: undefined, message: fallback };
        }
        return {
          ok: true,
          templateId: data.templateId,
          version: data.version ?? null,
          recorded: !!data.recorded,
        };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
